import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import { axiosJSONInstanceWithToken } from "@/helpers/axios";
import Swal from "sweetalert2";

const schema = yup.object().shape({
    expenses: yup.array().of(
        yup.object().shape({
            expense_name: yup.string().required("Expense Name is required"),
            expense_value: yup.number().typeError("Must be a number").required("Expense Value is required"),
            expense_qty: yup.number().typeError("Must be a number").required("Expense Quantity is required"),
            expense_type: yup.string().required("Expense Type is required"),
        })
    ),
});

const expensesTypes = [
    {
        key: '1',
        value: 'Single Fee',
    },
    {
        key: '2',
        value: 'Per Night',
    },
    {
        key: '3',
        value: 'Per Guest',
    },
    {
        key: '4',
        value: 'Per Night Per Guest',
    },
];

const BtnModalExpenses = ({ expenses, setExpenses, expenseList, setExpenseList }) => {
    const location = useLocation();

    const { row } = location.state;

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: { expenses: expenseList },
        resolver: yupResolver(schema),
    });

    const { fields, append, remove } = useFieldArray({ control, name: "expenses" });

    const handleClose = () => setExpenses(false);

    const onSubmit = (data) => {
        try {
            axiosJSONInstanceWithToken.post(`/booking-expense`, data?.expenses).then(({ data }) => {
                if (data?.status)
                    Swal.fire(
                        {
                            title: "Success",
                            icon: "success",
                            text: data?.message,
                        }
                    );
                else
                    Swal.fire(
                        {
                            title: "Error",
                            icon: "error",
                            text: data?.message,
                        }
                    );
            });
        } catch (error) {
            console.error(error);
            Swal.fire(
                {
                    title: "Error",
                    icon: "error",
                    text: "Failed to save expenses",
                }
            );
        }
    };

    return (
        <div className="flex justify-center">
            {expenses && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-800 bg-opacity-75"
                    onClick={handleClose}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 border-b">
                            <h2 className="text-xl font-semibold">Extra Expenses</h2>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: "480px" }}>
                                {fields.map((expense, index) => (
                                    <div key={expense.id} className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium">Expense Name</label>
                                            <input
                                                {...register(`expenses.${index}.expense_name`)}
                                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                                                placeholder="Expense Name"
                                            />
                                            <p className="text-red-500 text-sm">{errors.expenses?.[index]?.expense_name?.message}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Expense Value</label>
                                            <input
                                                {...register(`expenses.${index}.expense_value`)}
                                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                                                placeholder="Expense Value"
                                            />
                                            <p className="text-red-500 text-sm">{errors.expenses?.[index]?.expense_value?.message}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Type</label>
                                            <select
                                                {...register(`expenses.${index}.expense_type`)}
                                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                                            >
                                                <option value="">Type</option>
                                                {
                                                    expensesTypes?.map((type) => (
                                                        <option key={type.key} value={type.value}>{type.value}</option>
                                                    ))
                                                }
                                            </select>
                                            <p className="text-red-500 text-sm">{errors.expenses?.[index]?.expense_type?.message}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Expense Quantity</label>
                                            <input
                                                {...register(`expenses.${index}.expense_qty`)}
                                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                                                placeholder="Expense Quantity"
                                            />
                                            <p className="text-red-500 text-sm">{errors.expenses?.[index]?.expense_qty?.message}</p>
                                        </div>
                                        {fields.length > 1 && (
                                            <div className="sm:col-span-2 flex justify-start">
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 text-white bg-red-500 rounded hover:bg-red-600 focus:outline-none"
                                                    onClick={() => remove(index)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="button"
                                        className="px-4 py-2 text-white bg-[#8ec639] rounded hover:bg-green-600 focus:outline-none"
                                        onClick={() => append(
                                            { expense_name: "", expense_value: "", status: 'pending', booking_id: row?.id, expense_qty: "", expense_type: "" },
                                        )}
                                    >
                                        Add More
                                    </button>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t flex justify-between">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-[#8ec639] rounded hover:bg-green-500 focus:outline-none"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BtnModalExpenses;
